<template>
  <Transition name="peek">
    <div
      v-if="visible"
      class="fixed inset-0 z-50 flex items-center justify-center"
      @click="$emit('close')"
      @touchmove.prevent
      @wheel.prevent
    >
      <div class="absolute inset-0 bg-black/40" />
      <div class="relative z-10 flex flex-col items-center w-[80vw] max-w-[400px]" @click.stop>
        <div v-if="isVideo" class="relative max-w-full">
          <!-- Thumbnail shows instantly; the video fades in over it once it starts playing. -->
          <img :src="photoSrc" class="max-w-full rounded-2xl max-h-[60vh] bg-black/20" alt="" />
          <video
            v-if="videoReady"
            :src="videoSrc"
            class="absolute inset-0 w-full h-full rounded-2xl object-cover transition-opacity duration-300"
            :class="videoPlaying ? 'opacity-100' : 'opacity-0'"
            autoplay
            playsinline
            muted
            loop
            @playing="videoPlaying = true"
            @waiting="videoPlaying = false"
          />
          <!-- Buffering spinner over the thumbnail until the video actually starts playing. -->
          <div
            v-if="videoReady && !videoPlaying"
            class="absolute inset-0 flex items-center justify-center"
          >
            <div
              class="w-9 h-9 rounded-full border-[3px] border-white/40 border-t-white animate-spin"
            ></div>
          </div>
        </div>
        <img
          v-else
          :src="photoSrc"
          class="max-w-full rounded-2xl max-h-[60vh] bg-black/20"
          alt=""
        />
        <div class="mt-1 w-full rounded-2xl bg-white/80 backdrop-blur-md overflow-hidden">
          <button
            class="w-full flex items-center gap-3 px-4 py-3 text-[16px] font-aktiv-grotesk text-left border-b border-grey-10 disabled:opacity-40 disabled:cursor-not-allowed"
            :disabled="isDownloading"
            data-test-id="gensapp-photodrop-longpress-download"
            data-event-tracking="gensapp-photodrop-longpress-download"
            @click="$emit('download')"
          >
            <img
              :src="
                isGic ? '/assets/icons/download-orange.svg' : '/assets/icons/download-lightblue.svg'
              "
              class="w-6 h-6"
              alt=""
            />
            Download
          </button>
          <button
            class="w-full flex items-center gap-3 px-4 py-3 text-[16px] font-aktiv-grotesk text-left"
            data-test-id="gensapp-photodrop-longpress-select"
            data-event-tracking="gensapp-photodrop-longpress-select"
            @click="$emit('select')"
          >
            <img
              :src="
                isGic
                  ? '/assets/icons/checked-checkbox.svg'
                  : '/assets/icons/checked-checkbox-blue.svg'
              "
              class="w-6 h-6"
              alt=""
            />
            Select
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
export default {
  name: 'PhotoLongPressMenu',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    photo: {
      type: Object,
      default: null,
    },
    isDownloading: {
      type: Boolean,
      default: false,
    },
    isGic: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['download', 'select', 'close'],
  data() {
    return {
      videoReady: false,
      videoPlaying: false,
    };
  },
  computed: {
    isVideo() {
      return this.photo?.type === 'video';
    },
    videoSrc() {
      if (!this.photo) return '';
      return this.photo.videoUrl || this.photo.url;
    },
    photoSrc() {
      if (!this.photo) return '';
      return this.photo.type === 'video'
        ? this.photo.videoThumbnailUrl
        : this.photo.previewUrl || this.photo.url;
    },
  },
  watch: {
    visible(val) {
      if (val && this.isVideo) {
        this.videoReady = false;
        this.videoPlaying = false;
        this.$nextTick(() => {
          this.videoReady = true;
        });
      } else if (!val) {
        this.videoReady = false;
        this.videoPlaying = false;
      }
    },
  },
};
</script>

<style scoped>
.peek-enter-active {
  transition: opacity 200ms ease-out;
}
.peek-leave-active {
  transition: opacity 150ms ease-in;
}
.peek-enter-from,
.peek-leave-to {
  opacity: 0;
}
.peek-enter-to,
.peek-leave-from {
  opacity: 1;
}
</style>
